import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Grid,
  Button,
  FormGroup,
  Switch,
  FormControlLabel,
  OutlinedInput,
  InputLabel,
  FormControl,
  Typography,
  Select,
  MenuItem,
  FormLabel,
  Tooltip,
  AppBar,
  Toolbar,
  IconButton,
} from '@mui/material';
import { ArrowBackIos, ArrowUpward } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { api } from '../../api/instance';
import { modules, formats } from '../../utils/TextEditor/TextEditorConfig';
import Time from '../../utils/TimeFormats';
import { useProjects } from '../../hooks/useProjects';
import ProjectImageUpload from './ProjectImageUpload';
import deleteUpload from '../../utils/deleteUpload';
import Loader from '../../components/Loader/Loader';

const ProjectEdit = () => {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('id');
  const [project, setProject] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { setUpdateProjects } = useProjects();
  const formRef = useRef();
  const location = useLocation();
  const navigate = useNavigate();

  const mode = location.state.mode;

  useEffect(() => {
    const getProjectDetails = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/projects/${projectId}`);
        console.log('PROJECT', response.data.project);
        setProject(response.data.project);
        setLoading(false);
      } catch (error) {
        setProject({});
        setLoading(false);
        console.log(error);
      }
    };

    if (mode === 'edit') {
      getProjectDetails();
    }
  }, [projectId, mode]);

  const formSubmit = async () => {
    setSubmitting(true);
    // project comes from the state
    if (mode === 'edit') {
      console.log('PROJECT', project);
      const response = await api.put(`/projects/${projectId}`, project);

      if (response.status === 200) {
        setSubmitting(false);
        // maybe I could show a message instead of redirection
        setUpdateProjects(true);
        navigate('/projects');
      } else {
        console.log('Could not update project');
        setTimeout(() => {
          setSubmitting(false);
        }, 1000);
      }
    }

    if (mode === 'create') {
      const response = await api.post('/projects', project);

      if (response.status === 201) {
        setSubmitting(false);
        // maybe I could show a message instead of redirection
        setUpdateProjects(true);
        navigate('/projects');
      } else {
        console.log('Could not create project');
        setTimeout(() => {
          setSubmitting(false);
        }, 1000);
      }
    }
  }; // CREATE/EDIT FORM SUBMIT


  const deleteProject = async () => {
    console.log('ID', projectId);
    // delete uploads if any
    if (project.images.length >= 1) {
      console.log('Deleted images');
      project.images.forEach(async (value, key) => {
        await deleteUpload(value.path, setProject);
        setProject({ ...project, images: [] });
      });
    }
    // delete project if on edit more and images are deleted
    if (mode === 'edit') {
      try {
        const response = await api.delete(`/projects/${projectId}`);
        if (response.status === 200) {
          console.log('project deleted');
          setProject({});
          setUpdateProjects(true);
          navigate('/projects');
        }
      } catch (error) {
        console.log('Could not delete project');
      }
    }
    // return to projects when on create mode
    if (mode === 'create') {
      console.log('Cancelled project creation');
      setProject({});
      setUpdateProjects(true);
      navigate('/projects');
    }
  }; // DELETE PROJECT

  const switchChange = (name, e) => {
    setProject({ ...project, [name]: e.target.checked });
  };

  const clickSubmit = () => {
    console.log(project);
    const form = formRef.current;
    form.dispatchEvent(new Event('submit'));
  };

  const scrollTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <Grid item xs={8} sx={{ marginBottom: '0.5rem' }}>
        <Link to="/projects">
          <Button color="primary">
            <ArrowBackIos /> PROJECTS
          </Button>
        </Link>
      </Grid>

      {loading ? (
        <Loader />
      ) : (
        <Grid item xs={12} sm={12} md={8}>
          <Typography variant="h4" className="lato" margin="normal">
            {project.name}
          </Typography>
          <Typography variant="caption">
            CREATED BY {project.createdBy ? project.createdBy.name : ''} {project.createdBy ? project.createdBy.surname : ''}
          </Typography>
          <br />
          <Typography variant="caption">LAST UPDATE {Time.dateFullFormat(project.lastUpdated)}</Typography>

          <form data-abide noValidate id="createForm" className="cell" onSubmit={(e) => formSubmit(e)} encType="multipart/form-data" ref={formRef}>
            <input type="text" className="is-hidden" name="createdBy" id="createdBy" aria-errormessage="dateCreatedErr" value="USERNAME" readOnly />

            <FormControl fullWidth margin="normal">
              <InputLabel htmlFor="name">Project Name</InputLabel>
              <OutlinedInput
                id="name"
                name="name"
                label="Project Name"
                onChange={(e) => setProject({ ...project, name: e.target.value })}
                value={project.name}
              />
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel id="projectTypeLabel">Project Type</InputLabel>
              <Select
                labelId="projectType"
                id="projectType"
                value={project.projectType}
                label="project Type"
                onChange={(e) => setProject({ ...project, projectType: e.target.value })}
              >
                <MenuItem value="Residential">Residential</MenuItem>
                <MenuItem value="Motor Retail">Motor Retail</MenuItem>
                <MenuItem value="Commercial">Commercial</MenuItem>
                <MenuItem value="Refurbishment and Extensions">Refurbishment and Extensions</MenuItem>
                <MenuItem value="Public Sector">Public Sector</MenuItem>
                <MenuItem value="Leisure">Leisure</MenuItem>
                <MenuItem value="Student Living">Student Living</MenuItem>
              </Select>
            </FormControl>

            <FormGroup row fullWidth margin="normal">
              <FormControlLabel
                control={
                  <Switch checked={project.featured || false} onChange={(e) => switchChange('featured', e)} value="isFeatured" color="primary" />
                }
                label="Featured"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={project.newUpcoming || false}
                    onChange={(e) => switchChange('newUpcoming', e)}
                    value="isNewUpcoming"
                    color="primary"
                  />
                }
                label="New Upcoming"
              />
            </FormGroup>

            <FormControl fullWidth margin="normal">
              <InputLabel htmlFor="additionalDescription">Additional description</InputLabel>
              <OutlinedInput
                id="additionalDescription"
                name="additionalDescription"
                label="additionalDescription"
                onChange={(e) => setProject({ ...project, additionalDescription: e.target.value })}
                value={project.additionalDescription}
              />
            </FormControl>

            <FormControl fullWidth margin="normal">
              <FormLabel htmlFor="description">Project Description</FormLabel>
              <ReactQuill
                value={project.description}
                placeholder="Enter your page Content"
                onChange={(e) => setProject({ ...project, description: e })}
                theme="snow"
                modules={modules}
                formats={formats}
              />
            </FormControl>
          </form>

          <ProjectImageUpload type="project" apiPath="/projects/upload" images={project && project.images} setter={setProject} data={project} />

          <AppBar
            position="relative"
            className="sticky"
            data-margin-bottom="0"
            data-sticky
            data-stick-to="bottom"
            color="default"
            style={{ top: 'auto', bottom: 0, borderRadius: '0.2rem', background: '#fff', marginBottom: '1rem' }}
          >
            <Toolbar>
              <div style={{ flex: 2 }}>
                <Tooltip title="Top" placement="top">
                  <IconButton fontSize="small" onClick={scrollTop} style={{ marginLeft: -12, marginRight: 20 }} color="primary" aria-label="Menu">
                    <ArrowUpward />
                  </IconButton>
                </Tooltip>
              </div>
              <div className="right" style={{ gap: '1rem', display: 'flex' }}>
                <Button fontSize="small" onClick={() => deleteProject()} style={{ flex: 1 }} color="error" aria-label="Menu">
                  {mode === 'edit' ? 'DELETE' : 'CANCEL'}
                </Button>
                <Button fontSize="small" onClick={() => formSubmit()} style={{ flex: 1 }} color="primary" aria-label="Menu">
                  {submitting ? 'Saving' : 'Save'}
                </Button>
              </div>
            </Toolbar>
          </AppBar>
        </Grid>
      )}
    </>
  );
};

export default ProjectEdit;
